import React, { FC, MouseEvent } from "react";
import styled from "@emotion/styled";
import {
  AppPopup,
  AppQueryPopupsController
} from "components/AppQueryPopups/AppQueryPopupsBloc";
import { IconLockJourney } from "src/constants/icons";
import { ColorScheme } from "components/UserJourneyOverview/lib";
import Translate from "components/Translate/Translate";

const Button = styled.button`
  background: white;
  color: var(--color-charcoal);
  font-size: 0.765rem;
  padding: 1rem 1.5rem;
  border-radius: 10rem;
  width: 100%;
  cursor: pointer;

  &:hover {
    background: var(--color-fog);
  }
`;

const Container = styled.div`
  position: relative;
  overflow: hidden;
  width: clamp(8.96875rem, calc(8.96875rem + 1vw), 9.96875rem);
  height: 12.31rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-radius: 1rem;
  box-shadow: var(--light-shadow);
  padding: 1rem;
  cursor: pointer;
  --bg-size: 100% 200%;
  --eclipse-journey-card-bg-position: center;
  background-image: var(--journey-card-bg-gradient);
  background-size: var(--bg-size);

  background-repeat: no-repeat;

  &[data-locked="true"] {
    &:hover {
      #card-content {
        filter: blur(3px);
      }
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
`;

const Title = styled.h6`
  text-transform: uppercase;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 1rem;
  margin: 0;
`;

const ButtonText = styled.span`
  font-weight: 500;
  font-size: 0.765rem;
`;

interface JourneyCardProps {
  journey: string;
  colorScheme: ColorScheme;
  locked?: boolean;
}

const LockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  z-index: 2;

  &:hover {
    background-color: rgba(0, 0, 0, 0.64);

    span {
      opacity: 1;
    }
  }
`;

const CardContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  filter: blur(0);
  transition: filter 0.3s ease;
  z-index: 2;

  &[data-locked="true"] {
    align-items: center;
    justify-content: center;
  }
`;

const LockIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  filter: drop-shadow(0px 0px 0px rgba(0, 0, 0, 0.2))
    drop-shadow(-3px 3px 9px rgba(0, 0, 0, 0.2))
    drop-shadow(-13px 11px 17px rgba(0, 0, 0, 0.17))
    drop-shadow(-28px 24px 22px rgba(0, 0, 0, 0.1))
    drop-shadow(-50px 43px 26px rgba(0, 0, 0, 0.03))
    drop-shadow(-79px 67px 29px rgba(0, 0, 0, 0));
`;

const LockedText = styled.span`
  color: white;
  font-size: 1.125rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-weight: 500;
`;

const Eclipse = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  overflow: hidden;
  z-index: 1;

  &::after {
    content: "";
    position: absolute;
    bottom: -100%;
    left: -100%;
    right: -100%;
    height: 200%;
    border-radius: 50%;
    background-size: 100% 500%;
    background-repeat: no-repeat;
    background-position: var(--eclipse-journey-card-bg-position);
    background-image: var(--journey-card-bg-eclipse);
  }
`;

export const JourneyCard: FC<JourneyCardProps> = ({
  journey,
  colorScheme,
  locked
}) => {
  const onButtonClick = () => {
    if (locked) return;

    AppQueryPopupsController.openPopup(AppPopup.chooseYourNextJourney, {
      additionalParameters: {
        journey,
        colorScheme
      }
    });
  };

  const onJourneySelect = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  return (
    <Container
      onClick={onButtonClick}
      data-color-scheme={colorScheme}
      data-locked={locked}
    >
      <CardContent data-locked={locked} id="card-content">
        <div />
        <TitleWrapper>
          <Title>{journey}</Title>
          {!locked && (
            <ButtonText>
              <Translate msg="button.learnMore" />
            </ButtonText>
          )}
        </TitleWrapper>
        <Button onClick={onJourneySelect} hidden={locked}>
          <Translate msg="select" />
        </Button>
      </CardContent>

      {locked && (
        <LockOverlay>
          <LockIconWrapper>
            <IconLockJourney />
            <LockedText>
              <Translate msg="locked" />
            </LockedText>
          </LockIconWrapper>
        </LockOverlay>
      )}
      <Eclipse data-color-scheme={colorScheme} />
    </Container>
  );
};
